import { useState, useEffect } from 'react'

/**
 * Advanced Tab - Developer & Performance Settings
 * Each setting actually affects app behavior
 */
export default function AdvancedTab({ profile, onUpdateProfile }) {
  const [developerMode, setDeveloperMode] = useState(false)
  const [hardwareAcceleration, setHardwareAcceleration] = useState(true)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [debugLogging, setDebugLogging] = useState(false)
  const [showConnectionInfo, setShowConnectionInfo] = useState(false)

  // Load settings from localStorage and apply them
  useEffect(() => {
    const devMode = localStorage.getItem('gjyl-developer-mode') === 'true'
    const hwAccel = localStorage.getItem('gjyl-hardware-acceleration') !== 'false'
    const reducedMo = localStorage.getItem('gjyl-reduced-motion') === 'true'
    const debugLog = localStorage.getItem('gjyl-debug-logging') === 'true'
    const showConn = localStorage.getItem('gjyl-show-connection') === 'true'

    setDeveloperMode(devMode)
    setHardwareAcceleration(hwAccel)
    setReducedMotion(reducedMo)
    setDebugLogging(debugLog)
    setShowConnectionInfo(showConn)

    // Apply settings immediately
    applyDeveloperMode(devMode)
    applyHardwareAcceleration(hwAccel)
    applyReducedMotion(reducedMo)
    applyDebugLogging(debugLog)
    applyConnectionInfo(showConn)
  }, [])

  // Apply Developer Mode - adds data attribute for CSS/JS to check
  const applyDeveloperMode = (enabled) => {
    document.documentElement.setAttribute('data-developer-mode', enabled.toString())
    window.GJYL_DEVELOPER_MODE = enabled
    if (enabled) {
      console.log('%c[GJYL] Developer Mode ENABLED', 'color: #00ad9f; font-weight: bold')
      console.log('User IDs and debug info will be visible. Right-click elements to copy IDs.')
    }
  }

  // Apply Hardware Acceleration - toggles CSS transform optimizations
  const applyHardwareAcceleration = (enabled) => {
    document.documentElement.setAttribute('data-hw-acceleration', enabled.toString())
    if (enabled) {
      document.documentElement.style.setProperty('--gpu-rendering', 'translateZ(0)')
    } else {
      document.documentElement.style.setProperty('--gpu-rendering', 'none')
    }
  }

  // Apply Reduced Motion - disables animations app-wide
  const applyReducedMotion = (enabled) => {
    document.documentElement.setAttribute('data-reduced-motion', enabled.toString())
    if (enabled) {
      // Add style to disable transitions/animations
      let style = document.getElementById('gjyl-reduced-motion-style')
      if (!style) {
        style = document.createElement('style')
        style.id = 'gjyl-reduced-motion-style'
        document.head.appendChild(style)
      }
      style.textContent = `
        [data-reduced-motion="true"] * {
          animation-duration: 0.001ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.001ms !important;
        }
      `
    } else {
      const style = document.getElementById('gjyl-reduced-motion-style')
      if (style) style.remove()
    }
  }

  // Apply Debug Logging - enables verbose console output
  const applyDebugLogging = (enabled) => {
    window.GJYL_DEBUG = enabled
    if (enabled) {
      console.log('%c[GJYL] Debug Logging ENABLED', 'color: #00ad9f; font-weight: bold')
      // Override console methods to add prefix
      if (!window._originalConsoleLog) {
        window._originalConsoleLog = console.log
        window._originalConsoleWarn = console.warn
      }
    }
  }

  // Apply Connection Info - shows connection status indicator
  const applyConnectionInfo = (enabled) => {
    document.documentElement.setAttribute('data-show-connection', enabled.toString())
    window.GJYL_SHOW_CONNECTION = enabled
    // Dispatch event for connection indicator component to listen
    window.dispatchEvent(new CustomEvent('gjyl-connection-toggle', { detail: enabled }))
  }

  const handleToggle = (key, value, setter, applyFn) => {
    setter(value)
    localStorage.setItem(key, value.toString())
    applyFn(value)
    window.dispatchEvent(new CustomEvent('advanced-setting-change', { detail: { key, value } }))
  }

  const handleClearCache = () => {
    const keysToKeep = ['gjyl-developer-mode', 'gjyl-hardware-acceleration', 'gjyl-reduced-motion', 'gjyl-debug-logging', 'gjyl-show-connection']
    const allKeys = Object.keys(localStorage)
    let cleared = 0

    allKeys.forEach(key => {
      if (!keysToKeep.includes(key) && key.startsWith('gjyl-')) {
        localStorage.removeItem(key)
        cleared++
      }
    })

    alert(`Cleared ${cleared} cached items. Some changes may require a page refresh.`)
  }

  const handleExportData = () => {
    const data = {
      profile: profile,
      settings: {
        developerMode,
        hardwareAcceleration,
        reducedMotion,
        debugLogging,
        showConnectionInfo
      },
      exportedAt: new Date().toISOString()
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gjyl-data-export-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 max-w-4xl">
      <h3 className="text-xl font-semibold text-white mb-6">Advanced Settings</h3>

      {/* Developer Mode */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-white">Developer Mode</div>
            <div className="text-sm text-[#64748b]">Show user IDs, message IDs, and enable copy ID on right-click</div>
          </div>
          <ToggleSwitch
            enabled={developerMode}
            onChange={(v) => handleToggle('gjyl-developer-mode', v, setDeveloperMode, applyDeveloperMode)}
          />
        </div>
      </div>

      {/* Hardware Acceleration */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-white">Hardware Acceleration</div>
            <div className="text-sm text-[#64748b]">Use GPU for rendering. Disable if experiencing visual glitches</div>
          </div>
          <ToggleSwitch
            enabled={hardwareAcceleration}
            onChange={(v) => handleToggle('gjyl-hardware-acceleration', v, setHardwareAcceleration, applyHardwareAcceleration)}
          />
        </div>
      </div>

      {/* Reduced Motion */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-white">Reduced Motion</div>
            <div className="text-sm text-[#64748b]">Minimize animations throughout the app for accessibility</div>
          </div>
          <ToggleSwitch
            enabled={reducedMotion}
            onChange={(v) => handleToggle('gjyl-reduced-motion', v, setReducedMotion, applyReducedMotion)}
          />
        </div>
      </div>

      {/* Debug Logging */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-white">Debug Logging</div>
            <div className="text-sm text-[#64748b]">Enable verbose console logging for troubleshooting</div>
          </div>
          <ToggleSwitch
            enabled={debugLogging}
            onChange={(v) => handleToggle('gjyl-debug-logging', v, setDebugLogging, applyDebugLogging)}
          />
        </div>
      </div>

      {/* Show Connection Info */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-white">Show Connection Status</div>
            <div className="text-sm text-[#64748b]">Display latency and connection quality indicator</div>
          </div>
          <ToggleSwitch
            enabled={showConnectionInfo}
            onChange={(v) => handleToggle('gjyl-show-connection', v, setShowConnectionInfo, applyConnectionInfo)}
          />
        </div>
      </div>

      {/* Data Management */}
      <div className="mt-8 pt-6 border-t border-[#1e3a5f]">
        <h4 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-4">
          Data Management
        </h4>

        <div className="flex gap-3">
          <button
            onClick={handleClearCache}
            className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white rounded text-sm font-medium transition-colors"
          >
            Clear Cache
          </button>
          <button
            onClick={handleExportData}
            className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white rounded text-sm font-medium transition-colors"
          >
            Export My Data
          </button>
        </div>
        <p className="text-xs text-[#64748b] mt-3">
          Clear cached data or export a copy of your profile and settings
        </p>
      </div>
    </div>
  )
}

/**
 * Toggle Switch Component
 */
function ToggleSwitch({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-[#00ad9f]' : 'bg-[#1e3a5f]'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}
